<template>
  <div class="w-full">
    <div
      class="bg-white/95 backdrop-blur-sm rounded-lg sm:rounded-xl shadow-lg border border-(--p-surface-200) p-2.5 sm:p-4 mb-2 sm:mb-4 sm:sticky top-[95px] z-50"
    >
      <div
        class="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 sm:gap-4"
      >
        <div class="flex flex-wrap items-center gap-2 sm:gap-2 w-full lg:w-auto">
          <div class="flex items-center gap-2 sm:gap-3">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-(--p-primary-500) to-(--p-primary-600) rounded-lg sm:rounded-xl flex items-center justify-center shadow-md"
            >
              <i class="pi pi-calendar text-white text-lg sm:text-xl"></i>
            </div>
            <div class="min-w-[140px] sm:min-w-[160px]">
              <div
                class="text-lg sm:text-xl lg:text-2xl font-bold text-(--p-anthracite-500) leading-tight"
              >
                {{ monthLabel }}
              </div>
              <div class="text-xs sm:text-sm text-(--p-anthracite-500) font-medium">
                {{ currentYear }}
              </div>
            </div>
          </div>

          <div class="flex items-center gap-1 ml-auto lg:ml-0">
            <Button
              icon="pi pi-chevron-left"
              severity="secondary"
              size="small"
              @click="goToPreviousMonth"
              v-tooltip.top="'Previous month'"
              class="!p-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
            />
            <Button
              :label="undefined"
              severity="primary"
              size="small"
              icon="pi pi-home"
              @click="goToToday"
              v-tooltip.top="'Go to current month'"
              :disabled="isCurrentMonth"
              class="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
            />
            <Button
              icon="pi pi-chevron-right"
              severity="secondary"
              size="small"
              @click="goToNextMonth"
              v-tooltip.top="'Next month'"
              class="!p-2 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-h-0"
            />
          </div>
        </div>

        <div
          class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full lg:w-auto"
        >
          <div v-if="!isMobile" class="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <div
              class="inline-flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 bg-(--p-primary-500) text-white rounded-md sm:rounded-lg text-[10px] sm:text-xs font-medium border border-blue-200"
            >
              <i class="pi pi-list text-[10px] sm:text-xs"></i>
              <span class="font-bold">{{ totalVisibleActions }}</span>
              <span class="hidden min-[1740px]:inline">{{
                totalVisibleActions === 1 ? 'Action' : 'Actions'
              }}</span>
            </div>
            <div
              v-if="overdueActionsCount > 0"
              class="inline-flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 bg-(--p-tangerine-500) text-(--p-anthracite-500) rounded-md sm:rounded-lg text-[10px] sm:text-xs font-medium border border-red-200"
            >
              <i class="pi pi-exclamation-triangle text-[10px] sm:text-xs"></i>
              <span class="font-bold">{{ overdueActionsCount }}</span>
              <span class="hidden min-[1740px]:inline">Overdue</span>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 w-full sm:w-auto">
            <Button
              v-if="
                user &&
                (user as { access?: number }).access !== undefined &&
                (user as { access?: number }).access !== null &&
                (user as { access?: number }).access! >= 5
              "
              icon="pi pi-plus"
              label="New Action"
              severity="primary"
              size="small"
              @click="onCreateAction"
              class="flex-1 sm:flex-none min-h-[44px] sm:min-h-0"
            />
            <ActionFilter
              v-model="filterState"
              :plans="plans"
              @display-settings-changed="onDisplaySettingsChanged"
              class="flex-1 sm:flex-none"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Vertical Calendar -->
    <div v-if="isMobile" class="space-y-3">
      <div
        v-for="day in mobileCalendarDays"
        :key="day.key"
        class="bg-white rounded-lg shadow-md border border-(--p-surface-200) p-4"
        :class="getDayClasses(day)"
      >
        <div class="flex items-center justify-between mb-3 pb-3 border-b border-(--p-surface-200)">
          <div class="flex items-center gap-3">
            <div>
              <div class="text-base font-semibold text-(--p-anthracite-500)">
                {{ formatDayHeader(day.date) }}
              </div>
              <div
                v-if="dayCountMap.get(day.key)"
                class="flex items-center gap-1 text-xs text-(--p-anthracite-500) mt-0.5"
              >
                <i class="pi pi-list"></i>
                <span class="font-medium"
                  >{{ dayCountMap.get(day.key) }} action{{
                    (dayCountMap.get(day.key) || 0) === 1 ? '' : 's'
                  }}</span
                >
              </div>
            </div>
          </div>
          <Button
            icon="pi pi-plus"
            severity="primary"
            size="small"
            rounded
            class="min-h-[44px] min-w-[44px]"
            v-tooltip.top="'Add action for this day'"
            @click.stop="onCreateActionForDay(day.date)"
          />
        </div>
        <div class="flex flex-col gap-2">
          <template v-for="act in filteredActionsByDay.get(day.key) || []" :key="act.id">
            <button
              :ref="(el) => setActionRef(act.id, el)"
              class="action-card-mobile group w-full text-left px-3 py-3 rounded-lg hover:shadow-md transition-all duration-200 border-l-4 bg-white"
              :style="{
                borderLeftColor: resolveActionColor(act),
                backgroundColor: resolvedBg(resolveActionColor(act)),
              }"
              @click.stop="onActionClick(act)"
              @mouseenter="showActionPopover($event, act)"
              @mouseleave="hideActionPopover"
            >
              <div class="flex items-start justify-between gap-2 mb-2">
                <div class="flex items-center gap-2 flex-1">
                  <div
                    class="font-semibold text-base text-(--p-anthracite-500) group-hover:text-(--p-primary-700) transition-colors"
                  >
                    {{ act.title }}
                  </div>
                  <i
                    v-if="canSeePrivate && act.isPrivate"
                    class="pi pi-lock text-(--p-anthracite-500) text-[10px]"
                    v-tooltip.top="'Private action'"
                  ></i>
                </div>
                <div v-if="displaySettings.showStateBadge" class="flex-shrink-0">
                  <i
                    :class="getStateIcon(act.state)"
                    class="text-sm"
                    :style="{ color: getStateColor(act.state) }"
                  ></i>
                </div>
              </div>

              <div class="flex flex-wrap gap-2 items-center">
                <div
                  v-if="displaySettings.showDueDate"
                  class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded"
                  :class="
                    isOverdue(act)
                      ? 'bg-red-100 text-red-700 font-semibold'
                      : 'bg-(--p-surface-100) text-(--p-anthracite-500)'
                  "
                >
                  <i
                    :class="isOverdue(act) ? 'pi pi-exclamation-triangle' : 'pi pi-clock'"
                    class="text-xs"
                  ></i>
                  <span>{{ formatShort(act.dueDate) }}</span>
                </div>

                <div
                  v-if="displaySettings.showKpiArea && act.kpiArea"
                  class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded"
                  :class="getKpiAreaColorClass()"
                >
                  <i class="pi pi-chart-line text-xs"></i>
                  <span class="truncate">{{ act.kpiArea }}</span>
                </div>

                <div
                  v-if="
                    displaySettings.showRetailer &&
                    (getActionRetailer(act, plans) || getActionGroups(act, plans).length > 0)
                  "
                  class="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-(--p-primary-500) text-white"
                >
                  <i
                    :class="
                      getActionGroups(act, plans).length > 0
                        ? 'pi pi-users text-xs text-white'
                        : 'pi pi-building text-xs text-white'
                    "
                  ></i>
                  <span class="truncate">{{
                    getActionRetailer(act, plans) || getActionGroups(act, plans)[0]?.label
                  }}</span>
                </div>
              </div>
            </button>
          </template>

          <div
            v-if="
              !filteredActionsByDay.get(day.key) || filteredActionsByDay.get(day.key)?.length === 0
            "
            class="text-center py-6 text-sm text-(--p-anthracite-500)"
          >
            No actions for this day
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop Grid Calendar -->
    <div v-else class="calendar-grid shadow-md rounded-lg border-2 border-(--p-surface-200)">
      <div
        v-for="d in 7"
        :key="d"
        class="weekday-header bg-(--p-primary-500) px-1.5 sm:px-2 py-2 sm:py-3 text-center sm:sticky top-[180px] z-40"
      >
        <div
          class="text-[10px] sm:text-xs font-bold uppercase tracking-wide text-white hidden sm:block"
        >
          {{ weekdayLabels[d - 1] }}
        </div>
        <div class="text-[10px] font-bold uppercase tracking-wide text-white block sm:hidden">
          {{ weekdayLabels[d - 1].slice(0, 1) }}
        </div>
      </div>

      <div
        v-for="day in calendarDays"
        :key="day.key"
        class="calendar-day min-h-[100px] sm:min-h-[120px] lg:min-h-[140px] p-2 transition-all duration-200 overflow-hidden"
        :class="getDayClasses(day)"
        @dragenter.prevent="onDayDragEnter(day.key)"
        @dragleave.prevent="onDayDragLeave(day.key)"
        @dragover.prevent
        @drop="onDrop($event, day.date)"
        @click="onDayClick(day.date, $event)"
      >
        <div class="flex items-start justify-between mb-2">
          <div
            class="text-sm font-bold transition-all"
            :class="
              day.isToday
                ? 'text-white bg-(--p-primary-600) rounded-full w-7 h-7 flex items-center justify-center shadow-md'
                : day.inCurrentMonth
                  ? 'text-(--p-anthracite-500)'
                  : 'text-(--p-anthracite-500)'
            "
          >
            {{ day.date.getDate() }}
          </div>
          <div
            v-if="dayCountMap.get(day.key)"
            class="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
            :class="getCountBadgeClass(day.key)"
          >
            <i class="pi pi-list text-[8px]"></i>
            <span class="font-semibold">{{ dayCountMap.get(day.key) }}</span>
          </div>
        </div>
        <div class="flex flex-col gap-1.5">
          <template
            v-for="act in (filteredActionsByDay.get(day.key) || []).slice(0, 3)"
            :key="act.id"
          >
            <button
              :ref="(el) => setActionRef(act.id, el)"
              class="action-card-mini group w-full text-left px-2 py-1.5 rounded-md text-xs hover:shadow-md transition-all duration-200 border-l-4"
              :style="{
                borderLeftColor: resolveActionColor(act),
                backgroundColor: resolvedBg(resolveActionColor(act)),
              }"
              draggable="true"
              @dragstart="onDragStart($event, act)"
              @dragend="onDragEnd"
              @click.stop="onActionClick(act)"
              @mouseenter="showActionPopover($event, act)"
              @mouseleave="hideActionPopover"
            >
              <div class="flex items-start justify-between gap-1 mb-1 min-w-0">
                <div class="flex items-center gap-1 flex-1 min-w-0">
                  <div
                    class="truncate min-w-0 font-semibold text-(--p-anthracite-500) group-hover:text-(--p-primary-700) transition-colors"
                  >
                    {{ act.title }}
                  </div>
                  <i
                    v-if="canSeePrivate && act.isPrivate"
                    class="pi pi-lock text-(--p-anthracite-500) text-[9px] flex-shrink-0"
                    v-tooltip.top="'Private action'"
                  ></i>
                </div>
                <div v-if="displaySettings.showStateBadge" class="flex-shrink-0">
                  <i
                    :class="getStateIcon(act.state)"
                    class="text-[10px]"
                    :style="{ color: getStateColor(act.state) }"
                  ></i>
                </div>
              </div>

              <div class="flex flex-wrap gap-1 items-center">
                <div
                  v-if="displaySettings.showDueDate"
                  class="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded"
                  :class="
                    isOverdue(act)
                      ? 'bg-red-100 text-red-700 font-semibold'
                      : 'bg-(--p-surface-100) text-(--p-anthracite-500)'
                  "
                >
                  <i
                    :class="isOverdue(act) ? 'pi pi-exclamation-triangle' : 'pi pi-clock'"
                    class="text-[7px]"
                  ></i>
                  <span>{{ formatShort(act.dueDate) }}</span>
                </div>

                <div
                  v-if="displaySettings.showKpiArea && act.kpiArea"
                  class="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded max-w-[80px]"
                  :class="getKpiAreaColorClass()"
                >
                  <i class="pi pi-chart-line text-[7px]"></i>
                  <span class="truncate">{{ act.kpiArea }}</span>
                </div>

                <div
                  v-if="
                    displaySettings.showRetailer &&
                    (getActionRetailer(act, plans) || getActionGroups(act, plans).length > 0)
                  "
                  class="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded max-w-[70px] bg-(--p-primary-500) text-white"
                >
                  <i
                    :class="
                      getActionGroups(act, plans).length > 0
                        ? 'pi pi-users text-[7px] text-white'
                        : 'pi pi-building text-[7px] text-white'
                    "
                  ></i>
                  <span class="truncate">{{
                    getActionRetailer(act, plans) || getActionGroups(act, plans)[0]?.label
                  }}</span>
                </div>
              </div>
            </button>
          </template>

          <Button
            v-if="(dayCountMap.get(day.key) || 0) > 3"
            :label="`+${(dayCountMap.get(day.key) || 0) - 3} more`"
            icon="pi pi-ellipsis-h"
            severity="secondary"
            text
            size="small"
            class="w-full text-xs"
            @click.stop="openMore(day.key, $event)"
          />
        </div>
      </div>
    </div>
    <Popover ref="morePanel" class="custom-popover">
      <div class="min-w-[250px] max-w-[320px] p-2">
        <div class="mb-3 pb-2 border-b border-(--p-surface-200)">
          <div class="flex items-center gap-2 text-sm font-semibold text-(--p-anthracite-500)">
            <i class="pi pi-calendar"></i>
            <span>{{ moreDayLabel }}</span>
            <span class="text-xs bg-(--p-surface-200) px-2 py-0.5 rounded ml-auto">
              {{ moreItems.length }} {{ moreItems.length === 1 ? 'action' : 'actions' }}
            </span>
          </div>
        </div>

        <div class="flex flex-col gap-2 max-h-[400px] overflow-y-auto custom-scrollbar">
          <button
            v-for="act in moreItems"
            :key="act.id"
            class="action-card-mini group w-full text-left px-2 py-2 rounded-md text-xs hover:shadow-md transition-all duration-200 border-l-4"
            :style="{
              borderLeftColor: resolveActionColor(act),
              backgroundColor: resolvedBg(resolveActionColor(act)),
            }"
            @click="onMoreClick(act)"
          >
            <div class="flex items-start justify-between gap-1 mb-1">
              <div class="flex items-center gap-1">
                <div
                  class="font-semibold text-(--p-anthracite-500) group-hover:text-(--p-primary-700) transition-colors"
                >
                  {{ act.title }}
                </div>
                <i
                  v-if="canSeePrivate && act.isPrivate"
                  class="pi pi-lock text-(--p-anthracite-500) text-[9px]"
                  v-tooltip.top="'Private action'"
                ></i>
              </div>
              <i
                :class="getStateIcon(act.state)"
                class="text-[10px] flex-shrink-0"
                :style="{ color: getStateColor(act.state) }"
              ></i>
            </div>

            <div class="flex flex-wrap gap-1 items-center">
              <div
                class="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded"
                :class="
                  isOverdue(act)
                    ? 'bg-red-100 text-red-700 font-semibold'
                    : 'bg-(--p-surface-100) text-(--p-anthracite-500)'
                "
              >
                <i
                  :class="isOverdue(act) ? 'pi pi-exclamation-triangle' : 'pi pi-clock'"
                  class="text-[7px]"
                ></i>
                <span>{{ formatShort(act.dueDate) }}</span>
              </div>

              <div
                v-if="act.kpiArea"
                class="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded max-w-[100px]"
                :class="getKpiAreaColorClass()"
              >
                <i class="pi pi-chart-line text-[7px]"></i>
                <span class="truncate">{{ act.kpiArea }}</span>
              </div>
            </div>
          </button>
        </div>
      </div>
    </Popover>

    <Popover ref="actionPopover" class="action-details-popover" :dismissable="false">
      <div v-if="hoveredAction" class="min-w-[280px] max-w-[350px] p-1">
        <div class="mb-3 pb-3 border-b border-(--p-surface-200)">
          <div class="flex items-start gap-2 mb-2">
            <div
              class="w-2 h-12 rounded"
              :style="{ backgroundColor: hoveredAction.colorHex || '#60a5fa' }"
            ></div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <h4 class="font-bold text-base text-(--p-anthracite-500) truncate">
                  {{ hoveredAction.title }}
                </h4>
                <i
                  v-if="canSeePrivate && hoveredAction.isPrivate"
                  class="pi pi-lock text-(--p-anthracite-500) text-[10px]"
                  v-tooltip.top="'Private action'"
                ></i>
              </div>
              <div class="flex items-center gap-2">
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="getStateBadgeClass(hoveredAction.state)"
                >
                  <i :class="getStateIcon(hoveredAction.state)" class="text-[10px]"></i>
                  <span>{{ getStateLabel(hoveredAction.state) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="space-y-2 text-sm">
          <div class="flex items-center gap-2">
            <i class="pi pi-calendar text-(--p-anthracite-500)"></i>
            <span class="text-(--p-anthracite-500)">Due:</span>
            <span
              class="font-medium"
              :class="isOverdue(hoveredAction) ? 'text-red-600' : 'text-(--p-anthracite-500)'"
            >
              {{ formatLong(hoveredAction.dueDate) }}
            </span>
          </div>

          <div v-if="isOverdue(hoveredAction)" class="flex items-center gap-2 text-red-600">
            <i class="pi pi-exclamation-triangle"></i>
            <span class="font-semibold">OVERDUE</span>
          </div>

          <div v-if="hoveredAction.kpiArea" class="flex items-center gap-2">
            <i class="pi pi-chart-line text-(--p-anthracite-500)"></i>
            <span class="text-(--p-anthracite-500)">KPI:</span>
            <span class="font-medium px-2 py-0.5 rounded text-xs" :class="getKpiAreaColorClass()">{{
              hoveredAction.kpiArea
            }}</span>
          </div>

          <div
            v-if="
              getActionRetailer(hoveredAction, plans) ||
              getActionGroups(hoveredAction, plans).length > 0
            "
            class="flex items-center gap-2"
          >
            <i
              :class="
                getActionGroups(hoveredAction, plans).length > 0
                  ? 'pi pi-users text-(--p-anthracite-500)'
                  : 'pi pi-building text-(--p-anthracite-500)'
              "
            ></i>
            <span class="text-(--p-anthracite-500)"
              >{{ getActionGroups(hoveredAction, plans).length > 0 ? 'Group' : 'Retailer' }}:</span
            >
            <span class="font-medium text-(--p-anthracite-500)">
              {{
                getActionRetailer(hoveredAction, plans) ||
                (getActionGroups(hoveredAction, plans).length > 0
                  ? getActionGroups(hoveredAction, plans)
                      .map((g) => g.label)
                      .join(', ')
                  : '')
              }}
            </span>
          </div>

          <div v-if="getActionPlan(hoveredAction, plans)" class="flex items-center gap-2">
            <i class="pi pi-folder text-(--p-anthracite-500)"></i>
            <span class="text-(--p-anthracite-500)">Plan:</span>
            <span class="font-medium text-(--p-anthracite-500)">
              {{ getActionPlan(hoveredAction, plans)?.title }}
            </span>
          </div>

          <div v-if="hoveredAction.details" class="pt-2 border-t border-(--p-surface-200)">
            <div class="flex items-start gap-2">
              <i class="pi pi-align-left text-(--p-anthracite-500) mt-0.5"></i>
              <div class="flex-1">
                <span class="text-(--p-anthracite-500) block mb-1">Details:</span>
                <p class="text-(--p-anthracite-500) text-xs leading-relaxed line-clamp-3">
                  {{ hoveredAction.details }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-3 pt-3 border-t border-(--p-surface-200) text-xs text-(--p-anthracite-500)">
          Click to edit • Drag to reschedule
        </div>
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, toRefs, watch, onMounted, onUnmounted } from 'vue'
import type { Plan } from '@/config/types/Plan'
import type { Action } from '@/config/types/Action'
import useActionFilters from '@/composables/ActionFilters'
import useActions from '@/composables/Actions'
import useDisplaySettings from '@/composables/useDisplaySettings'
import useNotifications from '@/composables/Notification'
import useAuth from '@/composables/Auth'
import Popover from 'openvue/popover'

const props = defineProps<{
  plans: Plan[]
  year?: number
  month?: number
}>()

const emit = defineEmits<{
  (e: 'update:year', value: number): void
  (e: 'update:month', value: number): void
  (e: 'action:click', value: Action): void
  (e: 'action:move', payload: { action: Action; newDate: Date }): void
  (e: 'action:create'): void
  (e: 'day:create', value: Date): void
}>()

const state = reactive({
  currentYear: props.year ?? new Date().getFullYear(),
  currentMonth: props.month ?? new Date().getMonth(),
})

const dragState = reactive<{ draggingAction: Action | null; hoverKeys: Set<string> }>({
  draggingAction: null,
  hoverKeys: new Set<string>(),
})
const hoverVersion = ref(0)
const filterState = ref({
  searchQuery: '',
  selectedStates: ['pending', 'active', 'complete'],
  selectedKpiAreas: [] as string[],
  selectedDueDateFilters: [] as string[],
  selectedRetailers: [] as (string | number)[],
})

const { settings: displaySettings } = useDisplaySettings()
const { isOverdue, formatShort, filterActions } = useActionFilters()
const { user } = useAuth()
const canSeePrivate = computed(() => {
  const access = (user.value as { access?: number })?.access
  return access !== undefined && access !== null && access >= 5
})
const {
  getActionRetailer,
  getActionGroups,
  getActionPlan,
  getStateIcon,
  getStateColor,
  getStateBadgeClass,
  getStateLabel,
  formatLong,
  getKpiAreaColorClass,
} = useActions()
const { toast } = useNotifications()

const actionPopover = ref<InstanceType<typeof Popover> | null>(null)
const hoveredAction = ref<Action | null>(null)
const actionRefs = ref<Map<string | number, HTMLElement>>(new Map())
let popoverTimeout: ReturnType<typeof setTimeout> | null = null

const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
const isMobile = computed(() => windowWidth.value < 1280)

const handleResize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

watch(
  () => props.year,
  (v) => {
    if (typeof v === 'number') state.currentYear = v
  },
)

watch(
  () => props.month,
  (v) => {
    if (typeof v === 'number') state.currentMonth = v
  },
)

const monthLabel = computed(() =>
  new Date(state.currentYear, state.currentMonth, 1).toLocaleString('en-GB', { month: 'long' }),
)

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const firstDayOfMonth = computed(() => new Date(state.currentYear, state.currentMonth, 1))
const startOfGrid = computed(() => {
  const date = new Date(firstDayOfMonth.value)
  const dayOfWeek = date.getDay()
  date.setDate(date.getDate() - dayOfWeek)
  return date
})

const calendarDays = computed(() => {
  const days: Array<{ date: Date; inCurrentMonth: boolean; isToday: boolean; key: string }> = []
  const start = new Date(startOfGrid.value)
  for (let i = 0; i < 42; i++) {
    const date = new Date(start)
    date.setDate(start.getDate() + i)
    const inCurrentMonth = date.getMonth() === state.currentMonth
    const isToday = isSameDay(date, new Date())
    days.push({ date, inCurrentMonth, isToday, key: keyFromDate(date) })
  }
  return days
})

const mobileCalendarDays = computed(() => {
  return calendarDays.value.filter((day) => day.inCurrentMonth)
})

const isSameDay = (a: Date, b: Date) => {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

const keyFromDate = (d: Date) => {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

const toDate = (value: string | Date) => {
  return value instanceof Date ? value : new Date(value)
}

const flatActions = computed<Action[]>(() => {
  const list: Action[] = []
  for (const p of props.plans || []) {
    if (Array.isArray(p.actions)) list.push(...p.actions)
  }
  return list
})

const filteredActions = computed(() => {
  return filterActions(flatActions.value, filterState.value, props.plans)
})

const filteredActionsByDay = computed(() => {
  const map = new Map<string, Action[]>()
  for (const action of filteredActions.value || []) {
    const due = toDate(action.dueDate)
    const k = keyFromDate(due)
    const existingList = map.get(k) || []
    map.set(k, [...existingList, action])
  }
  for (const [k, list] of map) {
    const sortedList = [...list].sort(
      (a, b) =>
        toDate(a.dueDate).getTime() - toDate(b.dueDate).getTime() ||
        String(a.title).localeCompare(String(b.title)),
    )
    map.set(k, sortedList)
  }
  return map
})

const dayCountMap = computed(() => {
  const map = new Map<string, number>()
  for (const [k, list] of filteredActionsByDay.value) map.set(k, list.length)
  return map
})

const totalVisibleActions = computed(() => {
  return filteredActions.value.length
})

const overdueActionsCount = computed(() => {
  return filteredActions.value.filter((a) => isOverdue(a)).length
})

const completedActionsCount = computed(() => {
  return filteredActions.value.filter((a) => a.state === 'complete').length
})

const isCurrentMonth = computed(() => {
  const now = new Date()
  return state.currentMonth === now.getMonth() && state.currentYear === now.getFullYear()
})

const getDayClasses = (day: { inCurrentMonth: boolean; isToday: boolean; key: string }) => {
  const classes = []

  if (!day.inCurrentMonth) {
    classes.push('opacity-50')
  }

  if (hoverKeys.value.has(day.key)) {
    classes.push('ring-2 ring-(--p-primary-500) ring-offset-0 bg-(--p-primary-100)')
  } else if (day.isToday) {
    classes.push('bg-(--p-primary-50) border-l-4 border-l-(--p-primary-500)')
  } else {
    classes.push('bg-(--p-surface-0) hover:bg-(--p-surface-50)')
  }

  return classes.join(' ')
}

const getCountBadgeClass = (dayKey: string) => {
  const count = dayCountMap.value.get(dayKey) || 0
  const hasOverdue = (filteredActionsByDay.value.get(dayKey) || []).some((a) => isOverdue(a))

  if (hasOverdue) {
    return 'bg-red-100 text-red-700'
  } else if (count > 5) {
    return 'bg-orange-100 text-orange-700'
  } else {
    return 'bg-(--p-surface-200) text-(--p-anthracite-500)'
  }
}

// State utilities now come from Actions composable

const setActionRef = (actionId: string | number, el: unknown) => {
  if (el) {
    actionRefs.value.set(actionId, el as HTMLElement)
  }
}

const showActionPopover = (event: Event, action: Action) => {
  if (popoverTimeout) {
    clearTimeout(popoverTimeout)
  }

  popoverTimeout = setTimeout(() => {
    hoveredAction.value = action
    const target = event.target as HTMLElement
    if (target) {
      actionPopover.value?.show(event, target)
    }
  }, 300)
}

const hideActionPopover = () => {
  if (popoverTimeout) {
    clearTimeout(popoverTimeout)
    popoverTimeout = null
  }

  setTimeout(() => {
    actionPopover.value?.hide()
    hoveredAction.value = null
  }, 100)
}

// State badge class now comes from Actions composable

const onDragStart = (ev: DragEvent, action: Action) => {
  hideActionPopover()

  dragState.draggingAction = action
  try {
    ev.dataTransfer?.setData('application/json', JSON.stringify({ id: action.id }))
    ev.dataTransfer?.setDragImage((ev.target as HTMLElement) || document.body, 10, 10)
    ev.dataTransfer!.effectAllowed = 'move'
  } catch {}
}

const onDragEnd = () => {
  dragState.draggingAction = null
  dragState.hoverKeys.clear()
  hoverVersion.value++
}

const onDayDragEnter = (dayKey: string) => {
  dragState.hoverKeys.add(dayKey)
  hoverVersion.value++
}

const onDayDragLeave = (dayKey: string) => {
  dragState.hoverKeys.delete(dayKey)
  hoverVersion.value++
}

const onDrop = (ev: DragEvent, date: Date) => {
  ev.preventDefault()
  const action = dragState.draggingAction
  dragState.hoverKeys.clear()
  hoverVersion.value++
  if (!action) return

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const dropDate = new Date(date)
  dropDate.setHours(0, 0, 0, 0)

  if (dropDate < today) {
    toast(
      'Validation Error',
      'Cannot assign a due date in the past. Please select today or a future date.',
      'error',
    )
    return
  }

  emit('action:move', { action, newDate: new Date(date) })
}

const goToPreviousMonth = () => {
  let y = state.currentYear
  let m = state.currentMonth - 1
  if (m < 0) {
    m = 11
    y -= 1
  }
  state.currentYear = y
  state.currentMonth = m
  emit('update:year', y)
  emit('update:month', m)
}

const goToNextMonth = () => {
  let y = state.currentYear
  let m = state.currentMonth + 1
  if (m > 11) {
    m = 0
    y += 1
  }
  state.currentYear = y
  state.currentMonth = m
  emit('update:year', y)
  emit('update:month', m)
}

const goToToday = () => {
  const now = new Date()
  state.currentYear = now.getFullYear()
  state.currentMonth = now.getMonth()
  emit('update:year', state.currentYear)
  emit('update:month', state.currentMonth)
}

const onDisplaySettingsChanged = (settings: typeof displaySettings.value) => {
  displaySettings.value = { ...settings }
}

const onCreateAction = () => {
  emit('action:create')
}

const onActionClick = (action: Action) => {
  emit('action:click', action)
}

const onDayClick = (date: Date, ev: MouseEvent) => {
  const target = ev.target as HTMLElement | null
  if (target && target.closest('button')) return
  emit('day:create', new Date(date))
}

const onCreateActionForDay = (date: Date) => {
  emit('day:create', new Date(date))
}

const getOrdinalSuffix = (day: number): string => {
  if (day > 3 && day < 21) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

const formatDayHeader = (date: Date): string => {
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' })
  const day = date.getDate()
  const month = date.toLocaleDateString('en-GB', { month: 'long' })
  return `${weekday}, ${day}${getOrdinalSuffix(day)} ${month}`
}

const resolveActionColor = (action: Action) => {
  return action.colorHex || '#60a5fa'
}

const resolvedBg = (color: string) => {
  return `${color}20`
}

const { currentYear } = toRefs(state)
const hoverKeys = computed(() => {
  return dragState.hoverKeys
})

const morePanel = ref<InstanceType<typeof Popover> | null>(null)
const moreDayKey = ref<string | null>(null)
const moreItems = computed<Action[]>(() => {
  if (!moreDayKey.value) return []
  const list = filteredActionsByDay.value.get(moreDayKey.value) || []
  return list.slice(3)
})

const moreDayLabel = computed(() => {
  if (!moreDayKey.value) return ''
  const [year, month, day] = moreDayKey.value.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })
})

function openMore(dayKey: string, ev: MouseEvent) {
  moreDayKey.value = dayKey
  morePanel.value?.toggle(ev)
}

function onMoreClick(action: Action) {
  morePanel.value?.hide()
  onActionClick(action)
}
</script>

<style scoped>
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  background: var(--p-surface-300);
}

.weekday-header {
  transition: background-color 0.2s ease;
}

.weekday-header:hover {
  background: var(--p-primary-600);
}

.calendar-day {
  border: 1px solid var(--p-surface-200);
  cursor: pointer;
  overflow: hidden;
}

.calendar-day:hover {
  box-shadow: inset 0 0 0 2px var(--p-primary-200);
}

.action-card-mobile {
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.action-card-mobile:active {
  transform: scale(0.98);
  transition: transform 0.1s ease;
}

@media (max-width: 640px) {
  .calendar-day {
    min-height: 80px !important;
    padding: 0.5rem;
  }
}

.a11y-reduced-motion .calendar-day,
.a11y-reduced-motion .weekday-header {
  transition: none !important;
}

.a11y-high-contrast .calendar-day {
  border-width: 2px;
}

.dark-selector .calendar-day {
  background: var(--p-surface-800);
}

.dark-selector .calendar-day:hover {
  background: var(--p-surface-700);
}

.action-details-popover {
  z-index: 1100;
}

:deep(.action-details-popover .p-popover-content) {
  padding: 0.75rem;
}

:deep(.action-details-popover .p-popover) {
  position: fixed !important;
}

.custom-popover {
  z-index: 1100;
}

:deep(.custom-popover .p-popover) {
  position: fixed !important;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
